import type { Design } from './design';

export interface SaveResult { reference: string; previewUrl: string; renderUrl: string; imageUrls: Record<string, string> }

export class ApiError extends Error {
  constructor(public status: number, message: string) { super(message); }
}

/** Multipart: render.png, design.json, productId, and one `image_<id>` part per design.images entry. */
export function uploadDesign(apiBase: string, images: Record<string, Blob>, render: Blob, design: Design,
  onProgress: (fraction: number) => void): Promise<SaveResult> {
  const form = new FormData();
  form.append('render', render, 'render.png');
  form.append('design', new Blob([JSON.stringify(design)], { type: 'application/json' }), 'design.json');
  form.append('productId', design.productId);
  // The part's file name is the customer's own, so a rejected upload names the file in the API log.
  // A design reopened from the API is a plain Blob and keeps the generic name.
  for (const im of design.images) {
    const b = images[im.id];
    form.append(`image_${im.id}`, b, b instanceof File ? b.name : `image-${im.id}`);
  }
  // XHR rather than fetch: upload progress is the one thing fetch still cannot report.
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', `${apiBase}/api/designs`);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress(e.loaded / e.total);
    xhr.onerror = () => reject(new ApiError(0, 'network'));
    xhr.onload = () => {
      if (xhr.status === 201) resolve(JSON.parse(xhr.responseText));
      else reject(new ApiError(xhr.status, xhr.responseText));
    };
    xhr.send(form);
  });
}

export async function fetchDesign(apiBase: string, reference: string): Promise<SaveResult & { design: Design }> {
  const r = await fetch(`${apiBase}/api/designs/${reference}`);
  if (!r.ok) throw new ApiError(r.status, await r.text());
  return r.json();
}
